/**
 * Project documents slice (PX-W6.3, TASK-EDD04F2F).
 *
 * Cache keyed by project slug. Upload is optimistic-INSERT (the 201 response
 * IS the row — prepend it; a failed upload just surfaces the error), delete
 * is optimistic-FILTER (204 with no body to refetch for). List failures land
 * in `listError` with a Retry path.
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { projectFileService } from '../services/projectFileService.http'
import type { ProjectFileRow } from '../types'

export interface ProjectFilesError {
  key?: string
  message: string
}

export interface ProjectFilesState extends FetchCache {
  items: ProjectFileRow[]
  uploading: boolean
  uploadError: ProjectFilesError | null
  listError: ProjectFilesError | null
  deleting: string | null
}

const initialState: ProjectFilesState = {
  items: [],
  uploading: false,
  uploadError: null,
  listError: null,
  deleting: null,
  ...initialFetchCache
}

interface ProjectArg {
  orgId: string
  projectId: string
}

const toFilesError = (error: unknown): ProjectFilesError => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return { key: apiError.backendKey, message: apiError.message }
}

export const fetchFiles = createAsyncThunk<
  ProjectFileRow[],
  ProjectArg,
  { rejectValue: ProjectFilesError }
>('projectFiles/fetch', async ({ orgId, projectId }, { rejectWithValue }) => {
  try {
    return await projectFileService.list(orgId, projectId)
  } catch (error) {
    return rejectWithValue(toFilesError(error))
  }
}, {
  condition: ({ projectId }, { getState }) => {
    const { projectFiles } = getState() as { projectFiles: ProjectFilesState }
    return shouldFetch(projectFiles, projectId)
  }
})

/** Upload — the 201 row prepends (the list is newest first). */
export const uploadFile = createAsyncThunk<
  ProjectFileRow,
  ProjectArg & { file: File },
  { rejectValue: ProjectFilesError }
>('projectFiles/upload', async ({ orgId, projectId, file }, { rejectWithValue }) => {
  try {
    return await projectFileService.upload(orgId, projectId, file)
  } catch (error) {
    return rejectWithValue(toFilesError(error))
  }
})

/** Delete — fulfilled filters the row out (the endpoint answers 204, no body). */
export const deleteFile = createAsyncThunk<
  string,
  ProjectArg & { fileId: string },
  { rejectValue: ProjectFilesError & { fileId: string } }
>(
  'projectFiles/delete',
  async ({ orgId, projectId, fileId }, { rejectWithValue }) => {
    try {
      await projectFileService.remove(orgId, projectId, fileId)
      return fileId
    } catch (error) {
      const apiError = isApiError(error) ? error : toApiError(error)
      return rejectWithValue({
        key: apiError.backendKey,
        message: apiError.message,
        fileId
      })
    }
  }
)

export const projectFilesSlice = createSlice({
  name: 'projectFiles',
  initialState,
  reducers: {
    filesClosed(state) {
      state.items = []
      state.uploadError = null
      state.listError = null
      invalidate(state)
    },
    filesErrorDismissed(state) {
      state.uploadError = null
      state.listError = null
    },
    filesRetryArmed(state) {
      state.listError = null
      invalidate(state)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFiles.pending, (state, action) => {
        fetchStarted(state, action.meta.arg.projectId)
        state.listError = null
      })
      .addCase(fetchFiles.fulfilled, (state, action) => {
        // Key-scoped (TASK-3D6DB8B6): a superseded project's reply writes nothing.
        if (!fetchSucceeded(state, action.meta.arg.projectId)) return
        state.items = action.payload
      })
      .addCase(fetchFiles.rejected, (state, action) => {
        if (!fetchFailed(state, action.meta.arg.projectId)) return
        state.listError = action.payload ?? null
      })
      .addCase(uploadFile.pending, (state) => {
        state.uploading = true
        state.uploadError = null
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        state.uploading = false
        // Keyed prepend (review M-c): if the list fetch resolves AFTER this
        // upload (or the row is already known), the id is replaced in place /
        // kept once — never duplicated at the head.
        state.items = [
          action.payload,
          ...state.items.filter((row) => row.id !== action.payload.id)
        ]
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.uploading = false
        state.uploadError = action.payload ?? null
      })
      .addCase(deleteFile.pending, (state, action) => {
        state.deleting = action.meta.arg.fileId
        state.listError = null
      })
      .addCase(deleteFile.fulfilled, (state, action) => {
        state.deleting = null
        state.items = state.items.filter((row) => row.id !== action.payload)
      })
      .addCase(deleteFile.rejected, (state, action) => {
        state.deleting = null
        state.listError = action.payload ?? null
      })
  }
})

export const { filesClosed, filesErrorDismissed, filesRetryArmed } = projectFilesSlice.actions

/* ── Selectors ──────────────────────────────────────────────────────────── */

interface WithProjectFiles {
  projectFiles: ProjectFilesState
}

export const selectFiles = (s: WithProjectFiles) => s.projectFiles.items
export const selectFilesLoading = (s: WithProjectFiles) => s.projectFiles.loading
export const selectFilesUploading = (s: WithProjectFiles) => s.projectFiles.uploading
export const selectFilesUploadError = (s: WithProjectFiles) => s.projectFiles.uploadError
export const selectFilesListError = (s: WithProjectFiles) => s.projectFiles.listError
export const selectFilesDeleting = (s: WithProjectFiles) => s.projectFiles.deleting
